import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Slider } from "primereact/slider";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { getAddressFromCoordinates, getCurrentLocation, getWebsiteUrlForHome } from "../../../utils/stringUtils";
import { buildGoogleMapsSearchUrl, openUrlInCurrentTab, openUrlInNewTab } from "../../../utils/securityUtils";
import SearchAddress from "../../../components/general/SearchAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const DashboardListings = () => {
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isMobile;
  const navigate = useNavigate();
  const { backendAPIStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const [showListingDialog, setShowListingDialog] = useState<boolean>(false);
  const [selectedListingProfile, setSelectedListingProfile] = useState<any>(null);
  const [selectedCaregiverProfile, setSelectedCaregiverProfile] = useState<any>(null);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
  const [radiusInMiles, setRadiusInMiles] = useState<number>(20);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);

  const defaultValues = {
    latitude: 0,
    longitude: 0,
    address: '',
    radiusInMiles: 20
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  useEffect(() => {
    const fetchListingDetails = async () => {
      try {
        const location = await getCurrentLocation();
        if (!location) {
          throw new Error("Unable to fetch current location. Please allow location access and try again.");
        }

        const address = await getAddressFromCoordinates(location.latitude, location.longitude);
        setSelectedAddress(address!);
        reset({
          ...defaultValues,
          latitude: location.latitude,
          longitude: location.longitude,
        });
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoading(false);
      }
    };
    fetchListingDetails();
  }, [backendAPIStore, reset]);

  const handleAddressChange = (event: any) => {
    if (!event || !event.target || !event.target.value || event.target.value === "") {
      reset();
      setSelectedAddress("");
      return;
    }

    reset({
      ...defaultValues,
      latitude: event.target.latitude,
      longitude: event.target.longitude
    });
    setSelectedAddress(event.target.value);
  };

  const onSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);
    setSearchResults([]);
    setLastEvaluatedKey(null);

    if (!data.latitude || !data.longitude) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(), detail: 'Please select a valid address from the suggestions.', life: 3000 });
      setLoading(false);
      return;
    }

    try {
      const response = await backendAPIStore.searchListings({
        latitude: data.latitude,
        longitude: data.longitude,
        radiusInMiles: data.radiusInMiles,
        limit: 10,
        lastEvaluatedKey: lastEvaluatedKey
      });
      setSearchResults(response.items || []);
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
      setLatitude(data.latitude);
      setLongitude(data.longitude);
      setRadiusInMiles(data.radiusInMiles);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to search homes', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async (e) => {
    e.preventDefault();
    setLoadMoreLoading(true);

    try {
      const response = await backendAPIStore.searchListings({
        latitude: latitude,
        longitude: longitude,
        radiusInMiles: radiusInMiles,
        limit: 10,
        lastEvaluatedKey: lastEvaluatedKey
      });
      setSearchResults(prevSearchResults => [...prevSearchResults, ...(response.items || [])]);
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to load more results', life: 3000 });
    } finally {
      setLoadMoreLoading(false);
    }
  };

  const resultSubTitleTemplate = (rowData) => {
    return (
      <div className="flex justify-content-between align-items-center">
        {
          rowData.homeDetails.isWebsiteActive ? (
            <a target="_blank" rel="noopener noreferrer" href={getWebsiteUrlForHome(rowData.homeDetails)}>
              <div>{authExperience.discovery.jobWebsiteLabel}</div>
            </a>) : null
        }

      </div>
    );
  }

  const titleTemplate = (title) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900">{title}</span>
      </div>
    );
  };

  const resultFooterTemplate = (rowData) => {
    return (
      <div className="flex justify-content-between align-items-center grid">
        <div className="align-self-center d-flex justify-content-around col">
          <i className="pi pi-map-marker mt-3" style={{ cursor: 'pointer', marginRight: '1em' }} onClick={(e) => {
            e.preventDefault();
            const mapsUrl = buildGoogleMapsSearchUrl(rowData.homeDetails.latitude, rowData.homeDetails.longitude);
            if (mapsUrl) {
              openUrlInNewTab(mapsUrl);
            }
          }}></i>
          <i className="pi pi-phone mt-3" style={{ cursor: 'pointer', marginRight: '1em' }} onClick={(e) => {
            e.preventDefault();
            openUrlInCurrentTab(`tel:${rowData.userProfile.phoneNumber}`);
          }}></i>
          <i className="pi pi-envelope mt-3" style={{ cursor: 'pointer' }} onClick={(e) => {
            e.preventDefault();
            openUrlInCurrentTab(`mailto:${rowData.userProfile.email}`);
          }}></i>
        </div>
        <div className="col flex justify-content-end">
          <Button label="View Details" className="mt-2 p-button-outlined" onClick={(e) => {
            e.preventDefault();
            setSelectedListingProfile(rowData);
            setShowListingDialog(true);
          }} severity="warning" rounded />
        </div >
      </div>
    );
  }

  const resultTableSummaryTemplate = (rowData) => {
    return (
      <Card title={titleTemplate(rowData.homeDetails.businessName)} subTitle={resultSubTitleTemplate(rowData)}
        footer={resultFooterTemplate(rowData)} className="m-2 p-card-shadow" >
        <div>
          <p><strong>City:</strong> {rowData.homeDetails.city || "N/A"}</p>
          <div><strong>Description:</strong> {rowData.description || "N/A"}</div>
          <p><strong>Acceptance Criterias:</strong> {rowData.acceptanceCriterias.map((skill) => skill.name).join(", ") || "N/A"}</p>
          <p><strong>Non-Acceptance Criterias:</strong> {rowData.nonAcceptanceCriterias.map((cert) => cert.name).join(", ") || "N/A"}</p>
        </div>
      </Card >
    );
  }

  const handleReset = (e) => {
    e.preventDefault();
    reset(defaultValues);
    setSelectedAddress("");
    setSearchResults([]);
  }

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }} className="p-component">
      <Toast ref={toast}></Toast>
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          authExperience.discovery.listingsTitle,
          authExperience.discovery.listingsSubtitle,
          <div style={{ ...dashboardSectionCardStyle, padding: "0.75rem 1rem", boxShadow: "none" }}>
            Results: <strong className="ml-1">{searchResults.length}</strong>
          </div>,
        )}

        <div style={dashboardSectionCardStyle}>
          <div style={{ ...appAccentPanelStyle, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
            <div className="flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" }}>Listings Search Workspace</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  {authExperience.discovery.listingsWorkspaceSubtitle}
                </div>
              </div>
              <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 1rem" }}>
                <div style={{ fontSize: "0.78rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Search Radius</div>
                <div className="mt-2" style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a" }}>{radiusInMiles} miles</div>
              </div>
            </div>
          </div>

          <div style={{ padding: "1.25rem" }}>
          <form>
            <div>
              <div className="grid dashboard-search-grid">
                <div className="field col-12 lg:col-8 d-flex flex-column dashboard-search-location-field">
                  <label>Current Location</label>
                  <span className="p-float-label">
                    <SearchAddress name="address" id="address" onChange={handleAddressChange} required value={selectedAddress} />
                  </span>
                  {getFormErrorMessage('address')}
                </div>

                <div className="field col-12 lg:col-4 d-flex flex-column dashboard-search-radius-field">
                  <label>Search Radius (in miles)</label>
                  <span className="p-float-label">
                    <Controller
                      name="radiusInMiles"
                      control={control}
                      rules={{ required: "Search radius is required" }}
                      render={({ field }) => (
                        <div className="dashboard-radius-control">
                          <div className="mb-2 dashboard-radius-value">{field.value} miles</div>
                          <Slider value={field.value} onChange={(e) => field.onChange(e.value)} className="dashboard-radius-slider" style={{ cursor: "pointer" }}
                            min={10} max={100} step={10} />
                        </div>
                      )}
                    />
                  </span>
                  {getFormErrorMessage('radiusInMiles')}
                </div>
                <Divider type="dotted" />
                <div className="field col-12 d-flex flex-wrap justify-content-end gap-2">
                  <Button label="Search" className=" p-button-outlined" onClick={handleSubmit(onSubmit)} severity="warning" rounded />
                  <Button label="Reset" className="p-button-outlined" onClick={handleReset} severity="contrast" rounded />
                </div>
                <div className="p-error">{errorMessage}</div>
              </div>
              <div className="col-12 mt-2">
                {loading && (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                    <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                  </div>
                )}
                {!loading && searchResults.length > 0 ? (
                  <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem", padding: "0.9rem 1rem" }}>
                    <strong>{searchResults.length}</strong> {authExperience.discovery.listingsResultLabel} found within <strong>{radiusInMiles} miles</strong> of the selected location.
                  </div>
                ) : null}
                <div className="grid">
                  {
                    searchResults.map((home) => (
                      <div key={home.homeDetails.homeId} className="col-12 md:col-6 lg:col-4 p-2">
                        {resultTableSummaryTemplate(home)}
                      </div>))
                  }
                </div>
                {
                  lastEvaluatedKey && (
                    <div className="justify-content-center align-items-center flex">
                      <Button label="Load More" className="m-2 p-button-outlined " onClick={handleLoadMore} severity="info" rounded loading={loadMoreLoading} />
                    </div>
                  )
                }
              </div>
            </div>
          </form>
          </div>
        </div>
      </div>

      <Dialog
        header={renderSurfaceHeader("Listing Details", "Review provider contact information and availability notes.", "pi pi-home")}
        visible={showListingDialog}
        modal
        style={appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowListingDialog(false)}
      >
        <div>
          {selectedListingProfile ? (
            <div>
              <div style={appFormSectionStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Contact Details</div>
                <div className="grid mt-2" style={appMutedTextStyle}>
                  <div className="col-12 md:col-6"><strong>{authExperience.discovery.homeNameLabel}:</strong> {selectedListingProfile.homeDetails.businessName}</div>
                  <div className="col-12 md:col-6"><strong>Provider Name:</strong> <span style={{ textTransform: "capitalize" }}>{selectedListingProfile.userProfile.firstName} {selectedListingProfile.userProfile.lastName}</span></div>
                  <div className="col-12 md:col-6"><strong>Provider Contact:</strong> {selectedListingProfile.userProfile.phoneNumber || "N/A"}</div>
                  <div className="col-12 md:col-6"><strong>Provider Email:</strong> {selectedListingProfile.userProfile.email || "N/A"}</div>
                  <div className="col-12"><strong>Address:</strong> {selectedListingProfile.homeDetails.formattedAddress || "N/A"}</div>
                </div>
              </div>
              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Availability Details</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Description:</strong> {selectedListingProfile.description || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Acceptance Criterias:</strong> {selectedListingProfile.acceptanceCriterias.map((skill) => skill.name).join(", ") || "N/A"}</div>
                <div className="mt-2" style={appMutedTextStyle}><strong>Non-Acceptance Criterias:</strong> {selectedListingProfile.nonAcceptanceCriterias.map((cert) => cert.name).join(", ") || "N/A"}</div>
              </div>
            </div>
          ) : (
            <p>No profile selected.</p>
          )}
        </div>
      </Dialog>

    </div>
  );
};

export default DashboardListings;
